import React from 'react';
import { Linking, StyleSheet, View } from 'react-native';

import { Text, useTheme } from '@/components/ui';
import { CopyButton } from '@/components/ui/copy-button';

type MarkdownProps = {
	content?: string;
	children?: string;
	copyable?: boolean;
};

// Minimal, dependency-free Markdown renderer for RN/Expo.
// Supports: headings (#, ##, ###), bold **text**, italic *text* or _text_,
// inline code `code`, code blocks ``` ``` , links [text](url), and bullet lists.
export const Markdown: React.FC<MarkdownProps> = ({ content, children, copyable }) => {
	const theme = useTheme();
	const text = (content ?? children ?? '').toString();
	const lines = text.split(/\r?\n/);

	let inCodeBlock = false;
	const rendered = lines.map((line, idx) => {
		if (line.trim().startsWith('```')) {
			inCodeBlock = !inCodeBlock;
			return <View key={`code-sep-${idx}`} />;
		}

		if (inCodeBlock) {
			return (
				<View
					key={`code-${idx}`}
					style={{
						backgroundColor: theme.colors.surfaceVariant,
						padding: 8,
						borderRadius: 6,
						marginBottom: 4,
					}}
				>
					<Text style={{ fontFamily: 'monospace' }}>{line}</Text>
				</View>
			);
		}

		// Headings
		if (/^###\s+/.test(line)) {
			return <Text key={`h3-${idx}`} variant="titleSmall" style={{ fontWeight: '700', marginBottom: 4 }}>{line.replace(/^###\s+/, '')}</Text>;
		}
		if (/^##\s+/.test(line)) {
			return <Text key={`h2-${idx}`} variant="titleMedium" style={{ fontWeight: '700', marginBottom: 6 }}>{line.replace(/^##\s+/, '')}</Text>;
		}
		if (/^#\s+/.test(line)) {
			return <Text key={`h1-${idx}`} variant="titleLarge" style={{ fontWeight: '700', marginBottom: 8 }}>{line.replace(/^#\s+/, '')}</Text>;
		}

		// Lists
		if (/^\s*[-*]\s+/.test(line)) {
			const item = line.replace(/^\s*[-*]\s+/, '');
			return (
				<View key={`li-${idx}`} style={{ flexDirection: 'row', gap: 6, marginBottom: 2 }}>
					<Text>{'•'}</Text>
					<Text>{renderInline(item, theme)}</Text>
				</View>
			);
		}

		if (/^\s*\d+\.\s+/.test(line)) {
			const item = line.replace(/^\s*\d+\.\s+/, '');
			return (
				<View key={`ol-${idx}`} style={{ flexDirection: 'row', gap: 6, marginBottom: 2 }}>
					<Text>{'•'}</Text>
					<Text>{renderInline(item, theme)}</Text>
				</View>
			);
		}

		// Paragraph
		return <Text key={`p-${idx}`} style={{ marginBottom: 6 }}>{renderInline(line, theme)}</Text>;
	});

	return (
		<View style={copyable ? mdStyles.copyableContainer : undefined}>
			<View style={{ flex: 1 }}>{rendered}</View>
			{copyable && <CopyButton text={text} />}
		</View>
	);
};

function renderInline(text: string, theme: any): React.ReactNode[] {
	const nodes: React.ReactNode[] = [];
	let remaining = text;

	const patterns: Array<{ type: 'bold' | 'italic' | 'code' | 'link'; regex: RegExp }> = [
		{ type: 'link', regex: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/ },
		{ type: 'bold', regex: /\*\*([^*]+)\*\*/ },
		{ type: 'italic', regex: /(?:\*([^*]+)\*|_([^_]+)_)/ },
		{ type: 'code', regex: /`([^`]+)`/ },
	];

	while (remaining.length > 0) {
		let earliestMatch: { start: number; end: number; type: string; groups: string[] } | null = null;
		let matchType: 'bold' | 'italic' | 'code' | 'link' | null = null;

		for (const p of patterns) {
			const m = p.regex.exec(remaining);
			if (m) {
				const start = m.index;
				const end = m.index + m[0].length;
				if (!earliestMatch || start < earliestMatch.start) {
					earliestMatch = { start, end, type: p.type, groups: m.slice(1) as string[] };
					matchType = p.type;
				}
			}
		}

		if (!earliestMatch || !matchType) {
			nodes.push(remaining);
			break;
		}

		if (earliestMatch.start > 0) {
			nodes.push(remaining.slice(0, earliestMatch.start));
		}

		const captured = earliestMatch.groups;
		switch (matchType) {
			case 'bold':
				nodes.push(<Text key={`b-${nodes.length}`} style={{ fontWeight: '700' }}>{captured[0]}</Text>);
				break;
			case 'italic':
				nodes.push(<Text key={`i-${nodes.length}`} style={{ fontStyle: 'italic' }}>{captured[0] || captured[1]}</Text>);
				break;
			case 'code':
				nodes.push(
					<Text key={`c-${nodes.length}`} style={{ fontFamily: 'monospace', backgroundColor: theme.colors.surfaceVariant, paddingHorizontal: 4, borderRadius: 4 }}>
						{captured[0]}
					</Text>,
				);
				break;
			case 'link': {
				const label = captured[0];
				const url = captured[1];
				nodes.push(
					<Text
						key={`a-${nodes.length}`}
						style={{ color: theme.colors.primary, textDecorationLine: 'underline' }}
						onPress={() => Linking.openURL(url)}
					>
						{label}
					</Text>,
				);
				break;
			}
		}

		remaining = remaining.slice(earliestMatch.end);
	}

	return nodes;
}

const mdStyles = StyleSheet.create({
	copyableContainer: {
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
});

export default Markdown;

